/**
 * CrUX (Chrome UX Report) field-data hook — Google's public real-user
 * dataset for the site's origin, pulled daily by the crux-refresh
 * cron and served by `GET /v1/sites/{uuid}/crux` in one RTT
 * (coverage status + weekly series + latest period).
 *
 * Each series point is a 28-day rolling window snapshotted weekly, so
 * the data is smooth and lags sudden changes — deliberately different
 * from the pixel's daily RUM series. Spec:
 * plans/1.1.0/crux-origins/00-overview.md.
 */
import { type UseQueryResult, keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api';
import { useActiveOrg } from './active-org';
import { cruxKeys } from './query-keys';

export type CruxFormFactor = 'ALL' | 'PHONE' | 'DESKTOP';

/** One weekly collection period. p75s are ms except CLS (unitless);
 *  good/ni/poor are densities 0..1. */
export interface CruxPeriodRow {
  period_start: string;
  period_end: string;
  lcp_p75: number | null;
  lcp_good: number | null;
  lcp_ni: number | null;
  lcp_poor: number | null;
  inp_p75: number | null;
  inp_good: number | null;
  inp_ni: number | null;
  inp_poor: number | null;
  cls_p75: number | null;
  cls_good: number | null;
  cls_ni: number | null;
  cls_poor: number | null;
  fcp_p75: number | null;
  fcp_good: number | null;
  fcp_ni: number | null;
  fcp_poor: number | null;
  ttfb_p75: number | null;
  ttfb_good: number | null;
  ttfb_ni: number | null;
  ttfb_poor: number | null;
  // ── Phase A extras (01-phase-2.md) — optional during the deploy gap
  //    where the API predates these fields. ──
  /** Round-trip time p75 ms + low/medium/high densities. */
  rtt_p75?: number | null;
  rtt_low?: number | null;
  rtt_medium?: number | null;
  rtt_high?: number | null;
  /** LCP image subparts, p75 ms each. */
  lcp_img_ttfb_p75?: number | null;
  lcp_img_load_delay_p75?: number | null;
  lcp_img_load_duration_p75?: number | null;
  lcp_img_render_delay_p75?: number | null;
  /** Share-of-loads fractions, 0..1. */
  ff_phone?: number | null;
  ff_desktop?: number | null;
  ff_tablet?: number | null;
  nav_navigate?: number | null;
  nav_navigate_cache?: number | null;
  nav_back_forward?: number | null;
  nav_back_forward_cache?: number | null;
  nav_reload?: number | null;
  nav_restore?: number | null;
  nav_prerender?: number | null;
  lcp_rt_image?: number | null;
  lcp_rt_text?: number | null;
}

export interface CruxResponse {
  /** `pending` = the daily pull hasn't visited this site yet;
   *  `no_data` = origin isn't in the Chrome UX Report. */
  coverage: 'ok' | 'no_data' | 'pending';
  last_fetched_at: string | null;
  form_factor: CruxFormFactor;
  /** 0–100 field-experience score for the latest period (null if no
   *  metrics). Optional so a response from an API predating B2 still
   *  parses during the deploy gap. */
  field_score?: number | null;
  /** Google's CWV pass/fail for the latest period (null = can't
   *  assess). Optional for the same deploy-gap reason. */
  cwv?: 'pass' | 'fail' | null;
  latest: CruxPeriodRow | null;
  series: CruxPeriodRow[];
}

/** One monitored page's latest CrUX, for the worst-pages table. */
export interface CruxPageRow {
  page_uuid: string;
  name: string;
  url: string;
  period_end: string;
  field_score: number | null;
  cwv: 'pass' | 'fail' | null;
  lcp_p75: number | null;
  inp_p75: number | null;
  cls_p75: number | null;
  ttfb_p75: number | null;
  fcp_p75: number | null;
}

export interface CruxPagesResponse {
  pages: CruxPageRow[];
  monitored_count: number;
}

/** Per-page CrUX for a site's monitored pages (worst score first).
 *  Sparse — usually far fewer rows than `monitored_count`. */
export function useSiteCruxPages(
  siteUuid: string | null,
  opts: { enabled?: boolean } = {},
): UseQueryResult<CruxPagesResponse> {
  const orgId = useActiveOrg().orgId;
  return useQuery({
    queryKey: cruxKeys.pages(orgId, siteUuid ?? ''),
    enabled: !!siteUuid && (opts.enabled ?? true),
    placeholderData: keepPreviousData,
    staleTime: 60 * 60 * 1000,
    queryFn: () => apiFetch<CruxPagesResponse>(`/v1/sites/${siteUuid}/crux/pages`),
  });
}

export function useSiteCrux(
  siteUuid: string | null,
  opts: { formFactor?: CruxFormFactor; days?: number; enabled?: boolean } = {},
): UseQueryResult<CruxResponse> {
  const orgId = useActiveOrg().orgId;
  const { enabled, ...keyOpts } = opts;
  return useQuery({
    queryKey: cruxKeys.site(orgId, siteUuid ?? '', keyOpts),
    enabled: !!siteUuid && (enabled ?? true),
    placeholderData: keepPreviousData,
    // CrUX moves at most once a day — no point refetching per focus.
    staleTime: 60 * 60 * 1000,
    queryFn: () => {
      const sp = new URLSearchParams();
      if (keyOpts.formFactor && keyOpts.formFactor !== 'ALL')
        sp.set('form_factor', keyOpts.formFactor);
      if (keyOpts.days) sp.set('days', String(keyOpts.days));
      const qs = sp.toString();
      return apiFetch<CruxResponse>(`/v1/sites/${siteUuid}/crux${qs ? `?${qs}` : ''}`);
    },
  });
}

// ─── BigQuery monthly surface (spec 02-bigquery.md) ─────────────────
//
// Separate, monthly data source from the weekly `useSiteCrux` above:
// per-country breakdown, popularity rank, and multi-year history the
// History API can't provide. Paid-only — gate the UI with
// `useEntitlement('crux_geo')`; the endpoints 402 otherwise.

/** One country's latest-month CrUX (device = phone). */
export interface CruxGeoCountry {
  /** ISO 3166-1 alpha-2, lower-case ('us'). `<Flag>` accepts either case. */
  country_code: string;
  /** 0–100 field score (same weighting as the origin field_score). */
  field_score: number | null;
  cwv: 'pass' | 'fail' | null;
  lcp_p75: number | null;
  inp_p75: number | null;
  cls_p75: number | null;
  ttfb_p75: number | null;
  fcp_p75: number | null;
}

export interface CruxGeoResponse {
  coverage: 'ok' | 'no_data' | 'pending';
  last_fetched_at: string | null;
  /** Best field_score first, nulls last (server pre-sorts). */
  countries: CruxGeoCountry[];
}

/** One monthly collection period from the BigQuery origin history. Core
 *  metric columns mirror CruxPeriodRow; adds rank + the load split. */
export interface CruxMonthlyRow {
  /** First day of the collection month, 'YYYY-MM-DD'. */
  yyyymm: string;
  /** Coarse popularity magnitude bucket (1000, 10000, 1_000_000, …). */
  rank: number | null;
  lcp_p75: number | null;
  lcp_good: number | null;
  lcp_ni: number | null;
  lcp_poor: number | null;
  inp_p75: number | null;
  inp_good: number | null;
  inp_ni: number | null;
  inp_poor: number | null;
  cls_p75: number | null;
  cls_good: number | null;
  cls_ni: number | null;
  cls_poor: number | null;
  fcp_p75: number | null;
  fcp_good: number | null;
  fcp_ni: number | null;
  fcp_poor: number | null;
  ttfb_p75: number | null;
  ttfb_good: number | null;
  ttfb_ni: number | null;
  ttfb_poor: number | null;
  ff_phone: number | null;
  ff_desktop: number | null;
  ff_tablet: number | null;
}

export interface CruxHistoryResponse {
  coverage: 'ok' | 'no_data' | 'pending';
  last_fetched_at: string | null;
  /** Latest month's popularity rank — the hero popularity line. */
  rank: number | null;
  field_score: number | null;
  cwv: 'pass' | 'fail' | null;
  latest: CruxMonthlyRow | null;
  /** Ascending by month. */
  series: CruxMonthlyRow[];
}

/** Per-country latest CrUX for the world map + top/worst lists. Paid. */
export function useSiteCruxGeo(
  siteUuid: string | null,
  opts: { enabled?: boolean } = {},
): UseQueryResult<CruxGeoResponse> {
  const orgId = useActiveOrg().orgId;
  return useQuery({
    queryKey: cruxKeys.geo(orgId, siteUuid ?? ''),
    enabled: !!siteUuid && (opts.enabled ?? true),
    placeholderData: keepPreviousData,
    staleTime: 60 * 60 * 1000,
    queryFn: () => apiFetch<CruxGeoResponse>(`/v1/sites/${siteUuid}/crux/geo`),
  });
}

/** Multi-year monthly origin history + popularity rank. Paid. */
export function useSiteCruxHistory(
  siteUuid: string | null,
  opts: { months?: number; enabled?: boolean } = {},
): UseQueryResult<CruxHistoryResponse> {
  const orgId = useActiveOrg().orgId;
  const { enabled, ...keyOpts } = opts;
  return useQuery({
    queryKey: cruxKeys.history(orgId, siteUuid ?? '', keyOpts),
    enabled: !!siteUuid && (enabled ?? true),
    placeholderData: keepPreviousData,
    staleTime: 60 * 60 * 1000,
    queryFn: () => {
      const sp = new URLSearchParams();
      if (keyOpts.months) sp.set('months', String(keyOpts.months));
      const qs = sp.toString();
      return apiFetch<CruxHistoryResponse>(
        `/v1/sites/${siteUuid}/crux/history${qs ? `?${qs}` : ''}`,
      );
    },
  });
}
